import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get } from 'lodash';

import Header from 'components/Header';
import Transition from 'components/Transition';
import Container from 'components/Container';
import ContentWrapper from 'components/ContentWrapper';
import FormRow from 'components/Form/FormRow';
import FormColumn from 'components/Form/FormColumn';
import LoanCard from 'components/DPOnly/LoanCard';
import Progress from 'components/Progress';
import Modal from 'components/Modal';
import ApplicationId from 'components/ApplicationId';

import {
  newNextAction,
  NEW_NEXT,
} from 'actions/navigation';

import imgError from 'assets/images/error.svg';
import autopayIcon from 'assets/icons/autopay.svg';
import { useConfig } from 'config/ConfigContext';

const Review = (props) => {
  const config = useConfig();
  const { navigate, checkinData, params, searchParams } = props;
  const [isProgressVisible, setIsProgressVisible] = useState(false);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [uploadingPercentage, setUploadingPercentage] = useState(null);
  const [sourceData, setSourceData] = useState(null);
  const hasDownPayment = !!get(sourceData, 'offers', []).find((offer) => offer.downPaymentRequired);
  const workflowType = get(params, 'workflowType') || 'text2apply';
  const key = searchParams.get('key');

  useEffect(() => {
    const newSourceData = get(checkinData, ['data', 'data']);

    if (newSourceData === undefined) {
      const nextRoute = workflowType === 'text2apply' ? '' : `/${workflowType}`;
      navigate(`${nextRoute}/?key=${key}`);
    } else {
      setSourceData(newSourceData);
    }
  }, []);

  useEffect(() => {
    const { fetch } = props;

    if (fetch.isDone[`${NEW_NEXT}`] && !fetch.isRunning[`${NEW_NEXT}`]) {
      const routeUrl = checkinData.data && checkinData.data.url.replace(config.hostUrl, '');
      navigate(routeUrl);
    }
  }, [checkinData]);

  const handleSelect = (loanDetails) => {
    const { dispatch } = props;

    setIsProgressVisible(true);
    dispatch(newNextAction({
      key,
      workflowType,
      data: {
        term: get(loanDetails, ['term']),
        offerName: get(loanDetails, ['offerName']),
        subEntityType: get(checkinData, ['data', 'data', 'subEntityType']),
        subEntityId: get(checkinData, ['data', 'data', 'subEntityId']),
      },
      onUploadProgress: (progressEvent) => {
        setUploadingPercentage(Math.floor((progressEvent.loaded * 100) / progressEvent.total));
      },
      fail: (err) => {
        console.log(err);
        setIsPopupVisible(true);
        setIsProgressVisible(false);
      },
    }));
  };

  const handleClosePopup = () => {
    setIsPopupVisible(false);
  };

  const sortedOffers = useMemo(() => {
    const offers = get(sourceData, 'offers', []);
    return offers.sort((a, b) => {
      if (a.amountFinanced !== b.amountFinanced) {
        return b.amountFinanced - a.amountFinanced;
      }

      if (a.term !== b.term) {
        return a.term - b.term;
      }

      if (a.paymentAmount !== b.paymentAmount) {
        return a.paymentAmount - b.paymentAmount;
      }

      return b.isAutoPayOffer - a.isAutoPayOffer;
    });
  }, [sourceData]);

  const isOriginationPercentageExist = useMemo(() => {
    let originalPercExists = false;
    get(sourceData, 'offers', []).forEach((item) => {
      if (Number(item.originationPercentage)) {
        originalPercExists = true;
      }
    });
    return originalPercExists;
  }, [sourceData]);

  return (
    <Transition className="transition-item wrapper dponly-loan-options">
      <Header />
      <ApplicationId id={get(checkinData, 'attributes.entityId')} style={{ marginTop: 10, padding: '0 12px' }} />
      <ContentWrapper>
        <div className="heading">
          <h2>Select Loan Offer</h2>
          <div className="autopay-disclaimer">
            <img src={autopayIcon} width="52" height="52" alt="autopay icon" />
            <div className="autopay-promo-text">
              <h3>Save with AutoPay!</h3>
              <p>{`Get our Zero Interest Promotion* ${workflowType !== 'text2apply' ? 'and better terms ' : ''}by selecting "Continue with AutoPay"`}</p>
            </div>
          </div>
        </div>
        {
          sourceData && sourceData.hardPullDisclosure && (
            <div className="notification">
              Your previous loan status has changed. We now have a new offer for you. see below.
            </div>
          )
        }
        <div className="offers-container">
          <Container>
            <FormRow>
              <FormColumn>
                {
                  sourceData && sortedOffers.map((item, index) => (
                    <LoanCard
                      key={index}
                      loanDetails={item}
                      onSelect={handleSelect}
                      workflowType={workflowType}
                    />
                  ))
                }
              </FormColumn>
            </FormRow>
            <FormRow>
              <FormColumn>
                <div style={{ marginTop: 20 }}>
                  <small>
                    Terms are subject to change based on the first payment date that you will select.
                  </small>
                </div>
              </FormColumn>
            </FormRow>
            <FormRow>
              <FormColumn>
                <div style={{ marginTop: 20 }}>
                  <small>
                    <b style={{ color: '#0084f6' }}>Please Note:</b>
                    &nbsp;Enrolling in AutoPay is optional. The Bank&apos;s credit decision is not dependent on whether you enroll in AutoPay. If you continue with AutoPay, the method of payment that you use to make any applicable down payment will be the method of payment used for AutoPay. You can change your method of payment at any time in the future.
                  </small>
                </div>
              </FormColumn>
            </FormRow>
            <FormRow>
              <FormColumn>
                <div style={{ marginTop: 20 }}>
                  <small>
                    * Your loan may have a No Interest on Principal Option Promotion included. This promotion can save you money if you pay off the principal amount of the loan in full within the Promotional Period ("Promotional Period"). During the Promotional Period you will be responsible for making all of your monthly payments and your loan will accrue interest on a monthly basis. If you pay off your loan within the Promotional Period, the monthly payments that you have made during this period, which includes accrued interest, will be deducted from the principal amount of the loan. Length of Promotional Periods vary, please review your loan agreement for full details.
                  </small>
                </div>
              </FormColumn>
            </FormRow>
            {isOriginationPercentageExist && (
              <FormRow>
                <FormColumn>
                  <div style={{ marginTop: 20, paddingBottom: hasDownPayment ? 0 : 20 }}>
                    <small>
                      ** The Origination Fee may be up to 8% of the Amount Financed, is non-refundable, and is considered earned upon the funding of your loan. You can calculate the dollar amount of the Origination Fee for each offer listed above by multiplying the Amount Financed figure for that offer by .08 if the Origination Fee is 8%, by .05 if it is 5%, etc. For example, if the Origination Fee is 8% and if the Amount Financed is $1,000 the corresponding Origination Fee would be $80 ($1000 x .08 = $80). The total Principal Amount of your loan is the sum of the Amount Financed and the Origination Fee (i.e. if the Origination Fee is 8% and if the Amount Financed is $1,000 the Principal Amount of the loan would be $1080). Please review your Loan Agreement for additional terms and conditions.
                    </small>
                  </div>
                </FormColumn>
              </FormRow>
            )}
            {hasDownPayment && (
              <FormRow>
                <FormColumn>
                  <div style={{ marginTop: 20, paddingBottom: 20 }}>
                    <small>
                      <sup>1</sup>
                      &nbsp;In connection with your intended purchase, the merchant requires a down payment in the amount shown above. Your down payment to the merchant for your purchase is a condition to the fulfilment of your loan offer.
                    </small>
                  </div>
                </FormColumn>
              </FormRow>
            )}
          </Container>
        </div>
      </ContentWrapper>
      {
        isPopupVisible
          && (
          <Modal>
            <div className="card-content error-modal security-error">
              <img src={imgError} alt="" />
              <p>Offer is not selected</p>
              <div onClick={handleClosePopup} className="btn btn-red">OK</div>
            </div>
          </Modal>
          )
      }
      {
        isProgressVisible && <Progress percent={uploadingPercentage} />
      }
    </Transition>
  );
};

Review.propTypes = {
  dispatch: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
  fetch: PropTypes.object.isRequired,
  checkinData: PropTypes.object.isRequired,
  params: PropTypes.object.isRequired,
  searchParams: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  fetch: state.fetch,
  checkinData: state.screen.checkinData,
});

export default connect(mapStateToProps)(Review);
